/** @format */
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import {
	WINDOW,
	HttpService,
	CookieService,
	AnalyticsService,
	Logger,
	injectServerRequest
} from '@shared/data-access/services';
import { DCSG_APP_NAME } from '../constants/dcsg-app-name.token';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';

@Injectable({
	providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
	public readonly ANALYTICS_ERROR_MESSAGE_PREFIX = 'DCSG_NGX_GLOBAL_ERROR:';
	private readonly request = injectServerRequest();

	constructor(
		private analyticsService: AnalyticsService,
		@Inject(DCSG_APP_NAME) protected dcsgAppName: string,
		private cookieService: CookieService,
		private httpService: HttpService,
		private log: Logger,
		@Inject(PLATFORM_ID) protected platformId: any,
		@Inject(WINDOW) private readonly window: Window
	) {}

	handleError(error: Error | HttpErrorResponse) {
		// Error message
		let errorMessage: string;

		// Error details
		let errorDetails: object = {};

		const isBrowser = isPlatformBrowser(this.platformId);

		if (error instanceof HttpErrorResponse) {
			// Server or connection error happened
			if (isBrowser && !this.window.navigator.onLine) {
				/*
				 * Handle offline error
				 */
				// Set the error message
				errorMessage = `${DCSG_CONSTANTS.errorHandling.messagePrefixes.global.offline}${error.message}`;
			} else {
				/*
				 * Handle Http Error (error.status === 403, 404...)
				 */
				// Set the error message
				errorMessage = `${DCSG_CONSTANTS.errorHandling.messagePrefixes.global.http}${error.message}`;
			}

			try {
				// Set the error details
				errorDetails = {
					'X-ERROR-STATUS': `${error.status}`,
					'X-ERROR-ERROR':
						typeof error.error === 'object'
							? JSON.stringify(error.error)
							: `${error.error}`,
					'X-ERROR-HEADERS':
						typeof error.headers === 'object'
							? JSON.stringify(error.headers)
							: `${error.headers}`,
					'X-ERROR-MESSAGE': `${error.message}`,
					'X-ERROR-TYPE': `${error.type}`,
					'X-ERROR-NAME': `${error.name}`
				};
			} catch (error) {
				// Swallow the JSON stringify error
			}
		} else {
			/*
			 * Handle Client Error (Angular Error, ReferenceError...)
			 */
			// Set the generic error message
			errorMessage = `${DCSG_CONSTANTS.errorHandling.messagePrefixes.global.client}${error.message}`;

			// Set the error details
			errorDetails = {
				'X-ERROR-MESSAGE': `${error.message}`,
				'X-ERROR-NAME': `${error.name}`,
				'X-ERROR-STACK': `${error.stack}`
			};
		}

		// Set the DSG_UID
		errorDetails['DSG_UID'] = this.cookieService.get('DSG_UID');

		// Set the URL
		errorDetails['X-HREF'] =
			`${this.getUrlProtocol()}://${this.httpService.getHostName()}${this.httpService.getUrlPath()}${this.getUrlQueryString()}`;

		// Log the error
		this.log.error(`${errorMessage}`, errorDetails);

		// Check to see if context is browser
		if (isBrowser) {
			// Set and trigger analytics
			this.analyticsService.setErrorId(`${this.dcsgAppName}_GLOBAL_ERROR`);
			this.analyticsService.setErrorMessage(this.buildAnalyticsErrorMessage(errorDetails));
			this.analyticsService.triggerAnalyticsEvent('SiteError');
		}
	}

	private buildAnalyticsErrorMessage(errorDetails: object): string {
		return `${this.ANALYTICS_ERROR_MESSAGE_PREFIX}${JSON.stringify(errorDetails)}`;
	}

	/**
	 * @description
	 * Gets the current URL protocol
	 */
	public getUrlProtocol(): string {
		// URL protocol string
		let urlProtocol = '';

		// Check to see if browser or server
		if (!isPlatformBrowser(this.platformId)) {
			// Get the URL protocol from Express
			urlProtocol = this.request?.protocol.replace(':', '');
		} else {
			// Get the URL path from the window
			urlProtocol = this.window.location.protocol.replace(':', '');
		}

		// Return the URL protocol
		return urlProtocol;
	}

	/**
	 * @description
	 * Gets the current URL query string
	 */
	public getUrlQueryString(): string {
		// Check if running in the browser environment
		if (isPlatformBrowser(this.platformId)) {
			// Return the URL query string from the window
			return this.window.location.search;
		}

		// Get the URL query string from the server (Express)
		const urlArr = this.request?.originalUrl.split('?');
		return urlArr.length > 1 ? `?${urlArr[1]}` : '';
	}
}
